import { useState } from 'react';
import { Plus, Pencil, Trash2, Shield } from 'lucide-react';
import { useAlcada } from '@/features/compras/hooks/useAlcada';
import type { AlcadaDto, TipoAlcadaCompra } from '@/features/compras/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';

const tipoLabel: Record<TipoAlcadaCompra, string> = {
  ValorAte: 'Valor até',
  ValorIlimitado: 'Sem limite',
};

interface FormState {
  descricao: string;
  perfilId: number;
  tipoAlcada: TipoAlcadaCompra;
  valorLimite: number | '';
  isAtivo: boolean;
  ordem: number;
}

const emptyForm: FormState = {
  descricao: '',
  perfilId: 0,
  tipoAlcada: 'ValorAte',
  valorLimite: '',
  isAtivo: true,
  ordem: 1,
};

const AlcadasConfig = () => {
  const { alcadas, isLoading, criarAlcada, atualizarAlcada, removerAlcada, isCreating, isUpdating } = useAlcada();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editando,   setEditando]   = useState<AlcadaDto | null>(null);
  const [form,       setForm]       = useState<FormState>(emptyForm);

  const abrirCriar = () => {
    setEditando(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const abrirEditar = (a: AlcadaDto) => {
    setEditando(a);
    setForm({
      descricao:   a.descricao,
      perfilId:    a.perfilId,
      tipoAlcada:  a.tipoAlcada,
      valorLimite: a.valorLimite ?? '',
      isAtivo:     a.isAtivo,
      ordem:       a.ordem,
    });
    setDialogOpen(true);
  };

  const handleSalvar = async () => {
    const payload = {
      descricao:   form.descricao,
      perfilId:    form.perfilId,
      tipoAlcada:  form.tipoAlcada,
      valorLimite: form.valorLimite !== '' ? Number(form.valorLimite) : undefined,
      ordem:       form.ordem,
    };
    if (editando) {
      await atualizarAlcada({ id: editando.id, data: { ...payload, isAtivo: form.isAtivo } });
    } else {
      await criarAlcada(payload);
    }
    setDialogOpen(false);
  };

  const handleRemover = async (id: number) => {
    if (!confirm('Remover esta alçada?')) return;
    await removerAlcada(id);
  };

  const isSaving = isCreating || isUpdating;

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Alçadas de Aprovação</h1>
          <p className="text-muted-foreground mt-1">Configuração de faixas de valor para aprovação de compras</p>
        </div>
        <Button className="gap-2" onClick={abrirCriar}>
          <Plus className="h-4 w-4" />Nova Alçada
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-blue-500" />Regras Configuradas
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Ordem</TableHead>
                <TableHead>Descrição</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead className="text-right">Valor Limite</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-24">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Carregando...</TableCell>
                </TableRow>
              ) : alcadas.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    Nenhuma alçada configurada
                  </TableCell>
                </TableRow>
              ) : alcadas.map(a => (
                <TableRow key={a.id}>
                  <TableCell className="font-mono">{a.ordem}</TableCell>
                  <TableCell className="font-medium">{a.descricao}</TableCell>
                  <TableCell>{tipoLabel[a.tipoAlcada]}</TableCell>
                  <TableCell className="text-right font-mono">
                    {a.tipoAlcada === 'ValorIlimitado'
                      ? '∞'
                      : a.valorLimite?.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) ?? '—'}
                  </TableCell>
                  <TableCell>
                    <Badge className={a.isAtivo ? 'bg-green-500/10 text-green-600' : 'bg-gray-500/10 text-gray-600'}>
                      {a.isAtivo ? 'Ativo' : 'Inativo'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => abrirEditar(a)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleRemover(a.id)}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Dialog de criação/edição */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editando ? 'Editar Alçada' : 'Nova Alçada'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Descrição *</Label>
              <Input value={form.descricao} onChange={e => setForm(f => ({ ...f, descricao: e.target.value }))}
                placeholder="Ex: Diretor Financeiro" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Ordem</Label>
                <Input type="number" value={form.ordem}
                  onChange={e => setForm(f => ({ ...f, ordem: Number(e.target.value) }))} />
              </div>
              <div className="space-y-2">
                <Label>Tipo de Alçada</Label>
                <Select value={form.tipoAlcada}
                  onValueChange={v => setForm(f => ({ ...f, tipoAlcada: v as TipoAlcadaCompra }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ValorAte">Valor até</SelectItem>
                    <SelectItem value="ValorIlimitado">Sem limite</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {form.tipoAlcada === 'ValorAte' && (
              <div className="space-y-2">
                <Label>Valor Limite (R$) *</Label>
                <Input type="number" step="0.01" value={form.valorLimite}
                  onChange={e => setForm(f => ({ ...f, valorLimite: e.target.value ? Number(e.target.value) : '' }))} />
              </div>
            )}
            {editando && (
              <div className="flex items-center gap-3">
                <Switch checked={form.isAtivo}
                  onCheckedChange={v => setForm(f => ({ ...f, isAtivo: v }))} id="sw-ativo" />
                <Label htmlFor="sw-ativo">Ativo</Label>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button disabled={isSaving || !form.descricao} onClick={handleSalvar}>
              {isSaving ? 'Salvando...' : 'Salvar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AlcadasConfig;
